import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { ConnectMember, ConnectSettings, ConnectSettingsService } from '../connect-settings.service';
import {
  CONNECT_ROLE_OPTIONS, CONNECT_SETUP_SLOW_MS, connectRoleLabel, ConnectPanelState, connectPanelState, connectSetupIsSlow, connectSetupSteps,
} from '../connect-enable.util';

/**
 * Settings > MedicAI Connect. Before Connect: what it does, what it costs and how it switches on. While Connect sets
 * the practice up: progress, and the reason with a retry when it fails. Once on: who has access (and their role) and
 * who pays, read from and changed on Connect, and a way into Connect.
 */
@Component({
  selector: 'app-connect-panel',
  templateUrl: './connect-panel.component.html',
  styleUrls: ['./connect-panel.component.scss'],
})
export class ConnectPanelComponent extends BaseComponent implements OnChanges, OnDestroy {
  @Input() group: any;
  @Input() subscription: any;
  /** Start the enable flow (card update, then the access and billing dialog). */
  @Output() enable = new EventEmitter<void>();
  @ViewChild('memberDialog') memberDialog?: TemplateRef<any>;

  readonly roleOptions = CONNECT_ROLE_OPTIONS;
  readonly features = [
    { icon: 'send', text: 'Claims go straight to the medical aid from the patient file' },
    { icon: 'verified_user', text: 'Eligibility and benefit checks before you treat' },
    { icon: 'account_balance', text: 'Remittances matched to claims, shortfalls flagged' },
    { icon: 'forum', text: 'Chat with your billing team about a patient' },
  ];
  state: ConnectPanelState = 'none';

  /** What the panel shows: the set-up state, or only "could not be reached" when Connect could not be read. */
  get view(): ConnectPanelState | 'unreachable' {
    return this.state === 'provisioned' && this.loadFailed ? 'unreachable' : this.state;
  }
  slow = false;
  retrying = false;

  settings?: ConnectSettings;
  loading = false;
  loadFailed = false;
  notLinked = false;
  saving = false;
  groupUsers: any[] = [];
  /** The member being given access or having their role changed in the dialog. */
  editing: { userId: string | null; name: string; role: string; isNew: boolean } | null = null;
  billingShare = false;
  billingDay = 1;
  /** "Share all patients with my billing bureau" (Group.connectShareAllPatients). */
  shareAllPatients = false;

  private slowTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private connectSettings: ConnectSettingsService,
    private groupService: GroupService,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  get steps() {
    return connectSetupSteps(this.subscription);
  }

  /** Members with Connect access, the owner first. */
  get members(): ConnectMember[] {
    return (this.settings?.members || []).filter(m => m.isActive).sort((a, b) => Number(b.isOwner) - Number(a.isOwner));
  }

  /** Practice members who could be given Connect access. */
  get addable(): any[] {
    const withAccess = new Set(this.members.map(m => m.clinicalUserId));
    return this.groupUsers.filter(u => u.userId && !withAccess.has(u.userId));
  }

  get openConnectUrl(): string {
    return `${this.settings?.connectOrigin || ''}/login`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['group']) return;
    this.shareAllPatients = this.group?.connectShareAllPatients === true;
    const before = this.state;
    this.state = connectPanelState(this.group);
    this.watchSlow();
    if (this.state === 'provisioned' && (before !== 'provisioned' || changes['group'].previousValue?.id !== this.group?.id)) {
      this.load();
    }
  }

  override ngOnDestroy(): void {
    clearTimeout(this.slowTimer);
    super.ngOnDestroy();
  }

  /** Flags a waiting setup as slow once it has waited five minutes; a local timer, nothing is asked of the server. */
  private watchSlow(): void {
    clearTimeout(this.slowTimer);
    this.slow = this.state === 'pending' && connectSetupIsSlow(this.group);
    if (this.state !== 'pending' || this.slow) return;
    const waited = Date.now() - new Date(this.group.connectProvisionRequestedAt).getTime();
    this.slowTimer = setTimeout(() => this.watchSlow(), Math.max(CONNECT_SETUP_SLOW_MS - waited, 0) + 1000);
  }

  load(): void {
    if (!this.group?.id) return;
    this.loading = true;
    this.loadFailed = false;
    this.notLinked = false;
    this.connectSettings.load(this.group.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: settings => {
        this.settings = settings;
        this.billingShare = settings.billing?.arrangement === 'shared_clinical';
        this.billingDay = settings.billing?.billingDayOfMonth || 1;
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        if (err?.status === 403 && err?.error?.code === 'NOT_LINKED') this.notLinked = true;
        else this.loadFailed = true;
      },
    });
    this.groupService.getGroupUsers().pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      this.groupUsers = Array.isArray(res?.users) ? res.users : [];
    });
  }

  /**
   * Whether this admin can change the member from clinical: not the owner, not themselves (Connect refuses both), not
   * an account made on Connect itself (no clinical user to send), and not a role clinical does not give.
   */
  manageable(member: ConnectMember): boolean {
    return !!this.settings?.canManage
      && !member.isOwner
      && !!member.clinicalUserId
      && member.connectUserId !== this.settings.you
      && member.roles.length === 1 && this.roleOptions.some(o => o.value === member.roles[0]);
  }

  roleLabel(member: ConnectMember): string {
    if (member.isOwner) return 'Owner';
    return member.roles.map(connectRoleLabel).join(', ');
  }

  userLabel(user: any): string {
    return [user.title, user.name, user.surname].filter(Boolean).join(' ');
  }

  /** Give a practice member Connect access: pick the person and the role. */
  openAdd(): void {
    this.editing = { userId: null, name: '', role: 'viewer', isNew: true };
    this.dialog.open(this.memberDialog!, { width: '420px', maxWidth: '95vw', autoFocus: false });
  }

  /** Change a member's Connect role (double click, or tap and hold, on their chip). */
  openEdit(member: ConnectMember): void {
    if (!this.manageable(member)) return;
    this.editing = { userId: member.clinicalUserId, name: member.name, role: member.roles[0], isNew: false };
    this.dialog.open(this.memberDialog!, { width: '420px', maxWidth: '95vw', autoFocus: false });
  }

  saveEditing(): void {
    const editing = this.editing;
    if (!editing?.userId) return;
    this.change(
      this.connectSettings.setMember(this.group.id, editing.userId, editing.role),
      editing.isNew ? 'Connect access given.' : 'Connect role changed.',
      () => {
        this.dialog.closeAll();
        this.editing = null;
      },
    );
  }

  async removeMember(member: ConnectMember): Promise<void> {
    if (!member.clinicalUserId) return;
    const confirmed = await this.showConfirmationDialog(
      'Remove Connect access',
      `${member.name} will no longer be able to sign in to MedicAI Connect for this practice.`,
      undefined, 'Cancel', 'Remove',
    );
    if (!confirmed) return;
    this.change(this.connectSettings.removeMember(this.group.id, member.clinicalUserId), 'Connect access removed.');
  }

  saveBilling(): void {
    const billing = this.billingShare ? { shareSubscription: true, billingDayOfMonth: this.billingDay } : { shareSubscription: false };
    this.change(this.connectSettings.setBilling(this.group.id, billing), 'Connect billing saved.');
  }

  /** Who the bureau sees is the practice's decision, not Connect's: a practice admin may change it. */
  get canShare(): boolean {
    return String(this.group?.userRole || '').toLowerCase() === 'admin';
  }

  /** Saves who the bureau sees; turned on, the practice's patients start going to Connect. */
  saveSharing(): void {
    const share = this.shareAllPatients;
    this.saving = true;
    this.groupService.updateGroupSettings({ id: this.group.id, connectShareAllPatients: share }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.saving = false;
        this.showToastMessage(share
          ? 'Every patient will now be shared with your billing bureau.'
          : 'Only patients sent with a billing batch will be shared from now on.');
      },
      error: (err: any) => {
        this.saving = false;
        this.shareAllPatients = !share;
        this.showToastMessage(err?.error?.message || 'Could not save that change. Please try again.');
      },
    });
  }

  /** Runs a change on Connect, then reads the settings again; a refusal shows Connect's reason. */
  private change(request: Observable<any>, done: string, after?: () => void): void {
    this.saving = true;
    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.saving = false;
        after?.();
        this.showToastMessage(done);
        this.load();
      },
      error: (err: any) => {
        this.saving = false;
        this.showToastMessage(err?.error?.message || 'MedicAI Connect could not make that change. Please try again.');
      },
    });
  }

  retry(): void {
    if (this.retrying) return;
    this.retrying = true;
    this.connectSettings.retrySetup(this.group.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.retrying = false;
        this.groupService.patchSelectedGroup({
          connectProvisionStatus: 'pending', connectProvisionError: null, connectProvisionRequestedAt: new Date().toISOString(),
        });
      },
      error: err => {
        this.retrying = false;
        // Nothing saved to send again (enabled before the request was kept): start over from the dialog.
        if (err?.status === 409 && !this.group?.connectTenantId) {
          this.enable.emit();
          return;
        }
        this.showToastMessage(err?.error?.message || 'Could not send the Connect setup again. Please try again.');
      },
    });
  }
}
